// ANGEL FLIGHT — Router: a tiny DOM-screen manager.
//
// Owns one host element and a registry of screens. `show(id, data?)` unmounts
// the active screen (full DOM + listener teardown) and mounts the next one.
// Deliberately minimal: a single active screen, no deep history stack — just
// enough for Menu ↔ Hangar ↔ Game ↔ Debrief, plus Pause as an *overlay* shown
// over whatever is active (it does not replace it). App wires the navigation
// callbacks; the Router never imports App or the screens directly.

/** A mountable screen. `mount` builds its DOM under `parent`; `unmount` must
 *  fully remove it and drop every listener it added (no cross-nav leaks). */
export interface Screen<Data = unknown> {
  mount(parent: HTMLElement, data: Data): void;
  unmount(): void;
}

/** A factory that builds a fresh screen instance per `show` (screens are
 *  one-shot — built on entry, torn down on exit). */
export type ScreenFactory<Data = unknown> = () => Screen<Data>;

export class Router {
  private readonly host: HTMLElement;
  private readonly registry = new Map<string, ScreenFactory<unknown>>();
  private active: { id: string; screen: Screen<unknown> } | null = null;
  private overlay: { id: string; screen: Screen<unknown> } | null = null;

  /** @param host the element screens mount into (e.g. a UI layer over the canvas). */
  constructor(host: HTMLElement) {
    this.host = host;
  }

  /** Register a screen factory under an id. Re-registering replaces it. */
  register<Data>(id: string, factory: ScreenFactory<Data>): void {
    this.registry.set(id, factory as ScreenFactory<unknown>);
  }

  /** The id of the currently mounted (non-overlay) screen, if any. */
  get activeId(): string | null {
    return this.active?.id ?? null;
  }

  /**
   * Replace the active screen with `id`, passing `data` to its `mount`. The
   * previous active screen is unmounted first. Throws if `id` is unregistered.
   */
  show<Data>(id: string, data?: Data): void {
    const factory = this.registry.get(id);
    if (!factory) throw new Error(`[Router] unknown screen: ${id}`);
    this.clearActive();
    const screen = factory();
    screen.mount(this.host, data as unknown);
    this.active = { id, screen };
  }

  /**
   * Mount an overlay screen (e.g. Pause) ON TOP of the active screen without
   * replacing it. A second call replaces any existing overlay. Use for modal
   * layers that resume back to the underlying screen.
   */
  showOverlay<Data>(id: string, data?: Data): void {
    const factory = this.registry.get(id);
    if (!factory) throw new Error(`[Router] unknown overlay: ${id}`);
    this.clearOverlay();
    const screen = factory();
    screen.mount(this.host, data as unknown);
    this.overlay = { id, screen };
  }

  /** Tear down the current overlay (if any). Safe to call when none is shown. */
  clearOverlay(): void {
    if (!this.overlay) return;
    this.overlay.screen.unmount();
    this.overlay = null;
  }

  /** True while an overlay is mounted. */
  get hasOverlay(): boolean {
    return this.overlay !== null;
  }

  /** Unmount the active screen (and any overlay) — used when the Game view
   *  takes over the host, or on teardown. */
  clear(): void {
    this.clearOverlay();
    this.clearActive();
  }

  private clearActive(): void {
    if (!this.active) return;
    this.active.screen.unmount();
    this.active = null;
  }

  /** Full teardown: drops the active screen + overlay. */
  destroy(): void {
    this.clear();
    this.registry.clear();
  }
}
